import React, { useState } from 'react';
import Modal from '../../../Shared/Components/Modal/Modal';
import { createLearnerAsync, createClientAdminAsync } from '../../../Services/AccountService';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';

const CreateUserModal = ({ clientId, userType, groups, onSuccess }) => {
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        groupId: ''
    });

    const modalId = userType === 'Learner' ? 'createLearnerModal' : 'createClientAdminModal';
    const title = userType === 'Learner' ? 'Create New Learner' : 'Create New Client Admin';

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateUser = async () => {
        // Validation
        if (!formData.email || !formData.firstName || !formData.lastName) {
            showToast(false, 'All fields are required');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            showToast(false, 'Please enter a valid email address');
            return;
        }

        try {
            const data = {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                clientId: clientId,
                ...(formData.groupId && { groupId: formData.groupId }) // Only include if specified
            };

            let response;
            if (userType === 'Learner') {
                response = await createLearnerAsync(data);
            } else {
                response = await createClientAdminAsync(data);
            }

            if (response.success) {
                showToast(true, `${userType} created successfully`);
                setFormData({ email: '', firstName: '', lastName: '', groupId: '' });
                if (onSuccess) onSuccess();
            } else {
                showToast(false, response.errors?.[0]?.error || `Failed to create ${userType.toLowerCase()}`);
            }
        } catch (error) {
            console.error(`Error creating ${userType}:`, error);
            showToast(false, `Failed to create ${userType.toLowerCase()}`);
        }
    };

    const modalBody = (
        <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    fontWeight: '600',
                    marginBottom: '10px',
                    display: 'block',
                    color: '#1e293b',
                    fontSize: '0.95rem'
                }}>
                    Email <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={{
                        width: '100%',
                        height: '48px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        padding: '0 16px',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                    }}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    fontWeight: '600',
                    marginBottom: '10px',
                    display: 'block',
                    color: '#1e293b',
                    fontSize: '0.95rem'
                }}>
                    First Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="text"
                    name="firstName"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    style={{
                        width: '100%',
                        height: '48px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        padding: '0 16px',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                    }}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    fontWeight: '600',
                    marginBottom: '10px',
                    display: 'block',
                    color: '#1e293b',
                    fontSize: '0.95rem'
                }}>
                    Last Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="text"
                    name="lastName"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    style={{
                        width: '100%',
                        height: '48px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        padding: '0 16px',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                    }}
                />
            </div>

            <div>
                <label style={{
                    fontWeight: '600',
                    marginBottom: '10px',
                    display: 'block',
                    color: '#1e293b',
                    fontSize: '0.95rem'
                }}>
                    Group (Optional)
                </label>
                <select
                    name="groupId"
                    value={formData.groupId}
                    onChange={handleInputChange}
                    style={{
                        width: '100%',
                        height: '48px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        padding: '0 16px',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        fontFamily: 'inherit',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    <option value="">Auto-assign to default group</option>
                    {groups && groups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                </select>
                <div style={{
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    color: '#64748b',
                    fontStyle: 'italic'
                }}>
                    Leave empty to automatically assign to the default group
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            modalId={modalId}
            title={title}
            bodyText={modalBody}
            buttonText={`Create ${userType}`}
            buttonFunc={handleCreateUser}
        />
    );
};

export default CreateUserModal;
